'use client';

import React, { useEffect, useRef } from 'react';
import { usePortal } from './usePortal';
import { useScrollLock } from './useScrollLock';
import { useFocusTrap } from './useFocusTrap';
import { modalStack } from './modalStack';
import styles from './modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  labelledBy?: string;
  describedBy?: string;
  ariaLabel?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
  role?: 'dialog' | 'alertdialog';
  scrollable?: boolean;
};

export function Modal({
  isOpen,
  onClose,
  children,
  size = 'md',
  closeOnEscape = true,
  closeOnBackdrop = true,
  showCloseButton = true,
  labelledBy,
  describedBy,
  ariaLabel,
  initialFocusRef,
  className,
  role = 'dialog',
  scrollable = false
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const { render } = usePortal();

  const modalId = useRef(Symbol('modal'));

  useScrollLock(isOpen);
  useFocusTrap(containerRef, isOpen, initialFocusRef);

  useEffect(() => {
    if (!isOpen) return;
    const id = modalId.current;
    modalStack.push(id);
    return () => modalStack.remove(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Don't close if the escape originated from a file input (file dialog just closed)
        const target = event.target as HTMLElement;
        if (
          target?.tagName === 'INPUT' &&
          (target as HTMLInputElement).type === 'file'
        ) {
          return;
        }
        if (!modalStack.isTop(modalId.current)) return;
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeOnEscape, onClose]);

  // Track where mousedown started to prevent closing when file dialog closes
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleBackdropMouseDown = (event: React.MouseEvent) => {
    mouseDownTargetRef.current = event.target;
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (!closeOnBackdrop) return;
    // Only close if BOTH mousedown AND click were on the backdrop
    // This prevents the modal from closing when file dialogs close
    if (
      event.target === backdropRef.current &&
      mouseDownTargetRef.current === backdropRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  if (!isOpen) return null;

  const isFullSize = size === 'full';

  // Combine custom className with our module classes
  const dialogClassName = [
    styles.dialog,
    styles[size],
    scrollable ? styles.dialogScrollable : null,
    className
  ]
    .filter(Boolean)
    .join(' ');

  return render(
    <div
      ref={backdropRef}
      className={styles.backdrop}
      data-full-screen={isFullSize}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div
        ref={containerRef}
        className={dialogClassName}
        role={role}
        aria-modal
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            type="button"
            aria-label="Close modal"
            style={{
              position: 'absolute',
              top: '1.25rem',
              right: '1.25rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ds-text-secondary, #6b7280)',
              zIndex: 10
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
